import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCastContext } from '../context/CastContext';
import { castApi } from '../services/castApi';
import { JobStatus } from '../types';
import styles from '../Cast.module.css';

export const JobMonitor: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { dispatch } = useCastContext();
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const monitorJob = async () => {
      if (!jobId) return;

      try {
        const status = await castApi.getJobStatus(jobId);
        setJobStatus(status);
        dispatch({ type: 'UPDATE_JOB_STATUS', payload: status });

        // Continue monitoring if job is still in progress
        if (status.status === 'submitted' || status.status === 'processing') {
          setTimeout(() => monitorJob(), 2000); // Poll every 2 seconds
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to get job status');
      } finally {
        setLoading(false);
      }
    };

    monitorJob();
  }, [jobId, dispatch]);

  const handleBack = () => {
    navigate('/cast');
  };

  const handleViewAgent = () => {
    if (jobStatus?.result?.agent_id) {
      navigate(`/cast/${jobStatus.result.agent_id}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'completed_with_warnings':
        return 'warning';
      case 'failed':
        return 'error';
      case 'processing':
        return 'info';
      default:
        return 'pending';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'completed_with_warnings':
        return '⚠️';
      case 'failed':
        return '❌';
      case 'processing':
        return '🔄';
      default:
        return '⏳';
    }
  };

  if (loading) {
    return (
      <div className={styles['job-monitor-loading']}>
        <div className={styles['loading-spinner']}></div>
      </div>
    );
  }

  if (error || !jobStatus) {
    return (
      <div className={styles['job-monitor-error']}>
        <div className={styles['error-message']}>
          <h3>Error Loading Job</h3>
          <p>{error || 'Job not found'}</p>
          <button className={styles['back-button']} onClick={handleBack}>
            Back to Agents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['job-monitor']}>
      <div className={styles['monitor-header']}>
        <button className={styles['back-button']} onClick={handleBack}>
          ← Back to Agents
        </button>
        <h2>Job Status</h2>
      </div>

      <div className={styles['job-details']}>
        <div className={`${styles['status-card']} ${styles[getStatusColor(jobStatus.status)]}`}>
          <div className={styles['status-header']}>
            <span className={styles['status-icon']}>{getStatusIcon(jobStatus.status)}</span>
            <h3>Agent: {jobStatus.agent_name}</h3>
          </div>

          <div className={styles['status-info']}>
            <p><strong>Status:</strong> {jobStatus.status}</p>
            <p><strong>Job ID:</strong> {jobStatus.job_id}</p>
            <p><strong>Created:</strong> {new Date(jobStatus.created_at).toLocaleString()}</p>
            <p><strong>Updated:</strong> {new Date(jobStatus.updated_at).toLocaleString()}</p>
          </div>

          {jobStatus.progress && (
            <div className={styles['progress-info']}>
              <h4>Progress</h4>
              <p><strong>Step:</strong> {jobStatus.progress.step}</p>
              <p><strong>Message:</strong> {jobStatus.progress.message}</p>
            </div>
          )}

          {jobStatus.error && (
            <div className={styles['error-info']}>
              <h4>Error</h4>
              <p>{jobStatus.error}</p>
            </div>
          )}

          {jobStatus.result && (
            <div className={styles['result-info']}>
              <h4>Result</h4>
              <pre>{JSON.stringify(jobStatus.result, null, 2)}</pre>
            </div>
          )}
        </div>

        <div className={styles['job-actions']}>
          {jobStatus.status === 'completed' && jobStatus.result?.agent_id && (
            <button className={styles['view-agent-button']} onClick={handleViewAgent}>
              View Created Agent
            </button>
          )}

          <button className={styles['back-button']} onClick={handleBack}>
            Back to Agents
          </button>
        </div>
      </div>
    </div>
  );
};